import React, { useState } from 'react';
import { SecuritySettingsProps, StaffUser } from '../../../types';
import { FormField } from '../atoms';
import Button from '../../atoms/Button';
import './SecuritySettings.css';

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  user,
  onUpdate,
  onPasswordReset,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    role: user.role
  });
  
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
  };
  
  const handleRoleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only admin can change roles, and we're not allowing self-demotion
    if (user.role === 'admin') {
      const updatedUser: Partial<StaffUser> = {
        role: formData.role as StaffUser['role']
      };
      
      onUpdate(updatedUser);
    }
  };
  
  const handlePasswordReset = () => {
    onPasswordReset();
    setPasswordResetSent(true);
    
    // Reset the state after 5 seconds
    setTimeout(() => {
      setPasswordResetSent(false);
    }, 5000);
  };
  
  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ];
  
  return (
    <div className="security-settings">
      <div className="security-section">
        <h3>Account Security</h3>
        
        <div className="password-reset-section">
          <div className="password-reset-info">
            <h4>Password Reset</h4>
            <p>Send a password reset email to change your password.</p>
          </div>
          
          <Button
            onClick={handlePasswordReset}
            variant="secondary"
            disabled={loading || passwordResetSent}
          >
            {passwordResetSent ? 'Email Sent' : 'Reset Password'}
          </Button>
        </div>
        
        {passwordResetSent && (
          <div className="password-reset-message">
            A password reset email has been sent to {user.email}
          </div>
        )}
      </div>
      
      {user.role === 'admin' && (
        <form className="security-section" onSubmit={handleRoleSubmit}>
          <h3>Role & Permissions</h3>
          
          <div className="role-section">
            <FormField
              id="role"
              label="Account Role"
              type="select"
              value={formData.role}
              onChange={handleInputChange}
              options={roleOptions}
            />
            
            <div className="role-description">
              <h4>Role Permissions:</h4>
              <ul className="permissions-list">
                <li><strong>Admin:</strong> Full access to all features and settings</li>
                <li><strong>Manager:</strong> Can manage products, orders, and customers</li>
                <li><strong>Editor:</strong> Can edit products and view orders</li>
                <li><strong>Viewer:</strong> Read-only access to dashboard data</li>
              </ul>
            </div>
          </div>
          
          <div className="security-form-actions">
            <Button
              type="submit"
              disabled={loading || formData.role === user.role}
            >
              {loading ? 'Saving...' : 'Update Role'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SecuritySettings;
